import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NetworkCheckOutlinedIcon from "@mui/icons-material/NetworkCheckOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { brand, notice as tones } from "../../theme/businessFlowsTheme";
import { Caution, Hint, Refusal, Toast, focusRing } from "../workspaces/WorkspaceUi";
import { getPlatformSettings, resetPlatformSetting, savePlatformSetting, testPlatformSettings } from "../../actions";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

// Quick, client-side echoes of the backend's branding checks (services/platformSettings.js) -
// they save a round trip for an obviously wrong value; the backend still has the final say.
//
// A plain "starts with https://" check accepts a value like
// `https://acme.com/terms"><script>...` - all non-space, so it would slip
// past a \S+ regex and later break out of the href/src attribute it's
// inserted into. new URL() plus a check for quotes/angle brackets matches the
// backend's tightened validation.
const FORBIDDEN_CHARS = /["'<>]/;
const isHttpUrl = (t) => {
  if (FORBIDDEN_CHARS.test(t)) return false;
  try {
    return /^https?:$/i.test(new URL(t).protocol);
  } catch (err) {
    return false;
  }
};
// A relative logo path must start with exactly one slash - "//host/logo.png"
// is protocol-relative and would point at another site entirely.
const LOGO_PATH = /^\/(?!\/)\S+$/;
const isEmail = (t) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
const BRANDING_VALIDATORS = {
  "branding.primaryColour": (t) => (/^#[0-9a-f]{6}$/i.test(t) ? null : "Use #RRGGBB, e.g. #1a1a2e."),
  "branding.logoUrl": (t) => (!FORBIDDEN_CHARS.test(t) && (isHttpUrl(t) || LOGO_PATH.test(t)) ? null : "Start with https:// or / for a file on this instance."),
  "branding.supportEmail": (t) => (isEmail(t) ? null : "Enter a valid email address."),
  "branding.contactBcc": (t) => (/^none$/i.test(t) || isEmail(t) ? null : 'Enter a valid email address, or "none".'),
  "branding.companyUrl": (t) => (isHttpUrl(t) ? null : "Start with https://."),
  "branding.imprintUrl": (t) => (isHttpUrl(t) ? null : "Start with https://."),
  "branding.privacyUrl": (t) => (isHttpUrl(t) ? null : "Start with https://."),
  "branding.termsUrl": (t) => (isHttpUrl(t) ? null : "Start with https://."),
};

// Branding only: how the logo and colour a settings-in-progress will look,
// updated as the admin types (before Save, and without a round trip).
function BrandingPreview({ group, draft }) {
  const value = (key, fallback) => {
    const typed = draft[key];
    if (typeof typed === "string" && typed.trim()) return typed.trim();
    const setting = group.settings.find((s) => s.key === key);
    return (setting && setting.value) || fallback;
  };
  const logoUrl = value("branding.logoUrl", "/Business Flows_Logo_RGB.png");
  const productName = value("branding.productName", "Business Flows");
  const colourText = value("branding.primaryColour", "#1a1a2e");
  const validColour = /^#[0-9a-f]{6}$/i.test(colourText);
  const [logoOk, setLogoOk] = useState(true);

  useEffect(() => setLogoOk(true), [logoUrl]);

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{ mx: 2.5, mt: 2, mb: 0.5, p: 2, borderRadius: 3, bgcolor: validColour ? colourText : brand.ink, color: "#fff", transition: "background-color .15s ease" }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.14)",
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {logoOk ? (
          <Box component="img" src={logoUrl} alt={productName} onError={() => setLogoOk(false)} sx={{ maxWidth: "78%", maxHeight: "78%" }} />
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            No logo
          </Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
          {productName}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          Sign-in page preview · {validColour ? colourText : "not a valid colour yet"}
        </Typography>
      </Box>
    </Stack>
  );
}

// Where a value comes from, said plainly.
function SourceChip({ setting }) {
  const map = {
    console: {
      label: setting.updatedBy ? `Set here by ${setting.updatedBy.split("@")[0]}${setting.updatedAt ? `, ${formatDate(setting.updatedAt)}` : ""}` : "Set here",
      tone: tones.good,
    },
    env: { label: `From .env (${setting.env})`, tone: tones.quiet },
    default: { label: "Shipped default", tone: tones.quiet },
    unset: { label: "Not set", tone: tones.fix },
  };
  const look = map[setting.source] || map.unset;
  return (
    <Chip
      size="small"
      label={look.label}
      sx={{ height: 22, fontSize: "0.72rem", fontWeight: 650, color: look.tone.fg, bgcolor: look.tone.bg, border: `1px solid ${look.tone.line}`, borderRadius: 1.5 }}
    />
  );
}

// Where the key that encrypts stored secrets comes from - the environment, a
// file on this server, or one the server generated because nothing was
// configured. Never the key itself: the backend sends only this. A generated
// key is worth a word of warning (the file is the only copy), a configured one
// only a line of reassurance.
export function EncryptionKeyNotice({ encryptionKey, sx }) {
  if (!encryptionKey) return null;
  const { source, path, persisted, summary, advice } = encryptionKey;
  const Panel = source === "env" ? Hint : Caution;
  const title = source === "env" ? "Secrets are stored encrypted" : !persisted ? "The encryption key could not be saved" : source === "file" ? "Secrets are stored encrypted, with a key file on this server" : "This server made its own encryption key";
  return (
    <Panel sx={sx} title={title} next={advice || undefined}>
      {summary}
      {path && source !== "env" && (
        <>
          {" "}
          The file is{" "}
          <Box component="code" sx={{ fontSize: "0.82em", px: 0.5, py: 0.1, borderRadius: 1, bgcolor: "rgba(0,0,0,0.05)" }}>
            {path}
          </Box>
          .
        </>
      )}
    </Panel>
  );
}

function SettingRow({ setting, encryption, onSave, onReset, busy, validate, onDraftChange }) {
  const [draft, setDraft] = useState(setting.secret ? "" : setting.value || "");
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    setDraft(setting.secret ? "" : setting.value || "");
  }, [setting]);

  const changed = setting.secret ? draft.trim().length > 0 : draft.trim() !== (setting.value || "");
  const blocked = setting.secret && !encryption;
  const clientError = !setting.secret && validate && draft.trim() ? validate(draft.trim()) : null;

  return (
    <Box sx={{ py: 1.75 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1, md: 2 }} alignItems={{ md: "flex-start" }}>
        <Box sx={{ width: { md: 300 }, flexShrink: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {setting.secret && <LockOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />}
            <Typography variant="body2" sx={{ fontWeight: 650 }}>
              {setting.label}
            </Typography>
          </Stack>
          {setting.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {setting.description}
            </Typography>
          )}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <SourceChip setting={setting} />
            {setting.unreadable && (
              // Stored here, but not with the key this server has now: the
              // value is treated as not set, and says what to do about it.
              <Tooltip title={setting.message || "Enter it again."}>
                <Chip
                  size="small"
                  icon={<WarningAmberRoundedIcon />}
                  label="Can't be read"
                  sx={{ height: 22, fontSize: "0.72rem", fontWeight: 650, color: tones.fix.fg, bgcolor: tones.fix.bg, border: `1px solid ${tones.fix.line}`, borderRadius: 1.5 }}
                />
              </Tooltip>
            )}
            {setting.insecure && (
              <Tooltip title="This value was committed in the source code, so anyone with repo access knows it. Replace it here and on the other side.">
                <Chip
                  size="small"
                  icon={<WarningAmberRoundedIcon />}
                  label="Insecure"
                  sx={{ height: 22, fontSize: "0.72rem", fontWeight: 650, color: tones.wait.fg, bgcolor: tones.wait.bg, border: `1px solid ${tones.wait.line}`, borderRadius: 1.5 }}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              value={draft}
              disabled={blocked}
              onChange={(e) => {
                setDraft(e.target.value);
                onDraftChange?.(setting.key, e.target.value);
              }}
              type={setting.secret && !reveal ? "password" : "text"}
              error={Boolean(clientError)}
              helperText={clientError || " "}
              placeholder={
                blocked
                  ? "Set SETTINGS_ENCRYPTION_KEY first"
                  : setting.unreadable
                  ? setting.message
                  : setting.secret
                  ? setting.isSet
                    ? "Set - type a new value to replace it"
                    : "Not set"
                  : setting.env
                  ? `Empty - ${setting.env} applies`
                  : ""
              }
              autoComplete="off"
              InputProps={
                setting.secret
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setReveal((v) => !v)} aria-label={reveal ? "Hide" : "Show what you type"}>
                            {reveal ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : /Colour$/.test(setting.key)
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            aria-hidden
                            sx={{ width: 18, height: 18, borderRadius: 1, border: `1px solid ${brand.line}`, bgcolor: /^#[0-9a-f]{6}$/i.test(draft) ? draft : "transparent" }}
                          />
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={!changed || blocked || busy === setting.key || Boolean(clientError)}
            onClick={() => onSave(setting.key, draft)}
            sx={{ height: 40, flexShrink: 0 }}
          >
            {busy === setting.key ? <CircularProgress size={16} color="inherit" /> : "Save"}
          </Button>
          {setting.source === "console" && (
            <Tooltip title={setting.env ? `Go back to ${setting.env} from .env` : "Go back to the shipped default"}>
              <span>
                <IconButton size="small" onClick={() => onReset(setting.key)} disabled={busy === setting.key} sx={{ mt: 0.5 }} aria-label="Reset">
                  <UndoRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

// Integrations and addresses an admin changes here instead of in .env. Every
// change applies to the next request, with no deploy.
export default function PlatformSettings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [tests, setTests] = useState({});
  const [error, setError] = useState("");
  // `notice` names the theme's palette, so the toast's own text is `toast`.
  const [toast, setToast] = useState("");
  // Branding only: values as typed, for the live preview - not sent until Save.
  const [brandingDraft, setBrandingDraft] = useState({});

  const load = useCallback(async () => {
    const res = await getPlatformSettings();
    if (res?.success) setData(res.content);
    else setError(res?.msg || "Couldn't load the settings.");
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const apply = async (key, fn) => {
    setBusy(key);
    setError("");
    const res = await fn();
    setBusy("");
    if (res?.success) {
      setData(res.content);
      setToast(res.msg || "Saved.");
    } else setError(res?.msg || "That didn't work.");
  };

  const test = async (group) => {
    setTests((t) => ({ ...t, [group]: { running: true } }));
    const res = await testPlatformSettings(group);
    setTests((t) => ({ ...t, [group]: res?.success ? res.content : { ok: false, message: res?.msg || "The test didn't run." } }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal }}>
          <SettingsOutlinedIcon />
        </Box>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Platform settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Integrations and addresses, changed here instead of in .env. A change applies to the next request - no deploy, no
            restart.
          </Typography>
        </Box>
      </Stack>

      {data && !data.encryption && (
        <Caution sx={{ mb: 2 }} next="Other settings can be changed now.">
          Secrets can't be stored here until <strong>SETTINGS_ENCRYPTION_KEY</strong> is set in backend/.env.
        </Caution>
      )}
      {data && data.encryption && <EncryptionKeyNotice encryptionKey={data.encryptionKey} sx={{ mb: 2 }} />}
      {error && (
        <Refusal sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Refusal>
      )}

      <Stack spacing={2}>
        {!data
          ? [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={220} sx={{ borderRadius: 4 }} />)
          : data.groups.map((group) => {
              const result = tests[group.id];
              return (
                <Paper key={group.id} elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, overflow: "hidden" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ px: 2.5, py: 2, bgcolor: brand.shell }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }}>{group.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.description}
                      </Typography>
                    </Box>
                    {group.testable !== false && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={result?.running ? <CircularProgress size={14} color="inherit" /> : <NetworkCheckOutlinedIcon />}
                        disabled={Boolean(result?.running)}
                        onClick={() => test(group.id)}
                        sx={{ flexShrink: 0, bgcolor: "#fff" }}
                      >
                        Test connection
                      </Button>
                    )}
                  </Stack>
                  {group.id === "branding" && <BrandingPreview group={group} draft={brandingDraft} />}
                  {result && !result.running && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ px: 2.5, py: 1.25, bgcolor: result.ok ? tones.good.bg : tones.fix.bg, borderTop: `1px solid ${brand.hairline}` }}
                    >
                      {result.ok ? (
                        <CheckCircleRoundedIcon sx={{ color: tones.good.fg, fontSize: 18 }} />
                      ) : (
                        <ErrorRoundedIcon sx={{ color: tones.fix.fg, fontSize: 18 }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600, color: result.ok ? tones.good.fg : tones.fix.fg }}>
                        {result.message}
                      </Typography>
                    </Stack>
                  )}
                  <Divider />
                  <Stack divider={<Divider />} sx={{ px: 2.5 }}>
                    {group.settings.map((setting) => (
                      <SettingRow
                        key={setting.key}
                        setting={setting}
                        encryption={data.encryption}
                        busy={busy}
                        validate={BRANDING_VALIDATORS[setting.key]}
                        onDraftChange={group.id === "branding" ? (key, value) => setBrandingDraft((d) => ({ ...d, [key]: value })) : undefined}
                        onSave={(key, value) => apply(key, () => savePlatformSetting(key, value))}
                        onReset={(key) => apply(key, () => resetPlatformSetting(key))}
                      />
                    ))}
                  </Stack>
                </Paper>
              );
            })}
      </Stack>

      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}
